import { useMemo, useEffect, useRef, JSX } from "react";

import { Alert, Grid, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLocationsDataQuery } from "src/api/locationQueries.api";
import FootfallEvolution from "src/components/Dashboard/FootfallEvolution/FootfallEvolution";
import FootfallPerEntrance from "src/components/Dashboard/FootfallEvolution/FootfallPerEntrance";
import VisitorsMonthComponent from "src/components/Dashboard/InfoBar/VisitorsMonthComponent";
import VisitorsTodayComponent from "src/components/Dashboard/InfoBar/VisitorsTodayComponent";
import VisitorsYearComponent from "src/components/Dashboard/InfoBar/VisitorsYearComponent";
import OccupancyLastHourComponent from "src/components/Dashboard/LiveSection/OccupancyLastHourComponent";
import WeatherInfluenceOnFootfallComponent from "src/components/Dashboard/WeatherInfluenceOnFootfall/WeatherInfluenceOnFootfallComponent";
import { scrollGridItem } from "src/components/InfoBar/infoBar.helper";
import ScrollGridInfoBar from "src/components/InfoBar/ScrollGridInforBar";
import IntervalTabsWrapper from "src/components/IntervalTabs/IntervalTabsWrapper";
import SectionTitleWrapper from "src/components/SectionWrapper/SectionWrapper";
import ComponentId from "src/constants/componentId.enum";
import LocationType from "src/types/LocationType";

const componentId = ComponentId.IntervalTabsWithDatepicker;

const styles = {
  fullWidth: { xs: 12 },
  sectionMd4: { xs: 12, md: 4 },
  sectionMd6: { xs: 12, md: 6 },
  quarterWidth: { md: 4 },
};

function ShopDashboard(): JSX.Element {
  const { t } = useTranslation();
  const { locationId } = useParams();
  const { data: locations, isLoading } = useLocationsDataQuery();

  const shopLocations = useMemo(() => (locations ? locations.value.filter((location) => location.Type === "Shop") : []), [locations]);
  const selectedShop = useMemo(() => shopLocations.find((mall) => mall.Id === locationId), [locationId, shopLocations]);

  const visitorsMonthRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedShop && visitorsMonthRef.current) {
      visitorsMonthRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [selectedShop]);

  return (
    <>
      {isLoading && (
        <Grid size={styles.sectionMd4}>
          <LinearProgress />
        </Grid>
      )}

      {!isLoading && !selectedShop && (
        <Grid size={styles.sectionMd4}>
          <Alert severity='warning'>{t("The selected location is not a Shop")}</Alert>
        </Grid>
      )}

      {selectedShop && (
        <>
          <Grid size={styles.fullWidth}>
            <ScrollGridInfoBar>
              <Grid size={styles.quarterWidth} sx={scrollGridItem(300)}>
                <VisitorsTodayComponent selectedLocation={selectedShop} />
              </Grid>
              <Grid size={styles.quarterWidth} sx={scrollGridItem(300)} ref={visitorsMonthRef}>
                <VisitorsMonthComponent selectedLocation={selectedShop} />
              </Grid>
              <Grid size={styles.quarterWidth} sx={scrollGridItem(300)}>
                <VisitorsYearComponent selectedLocation={selectedShop} />
              </Grid>
            </ScrollGridInfoBar>
          </Grid>
          <IntervalTabsWrapper componentId={componentId} title={"Historic"}>
            <Grid container spacing={2}>
              <Grid size={styles.fullWidth}>
                <FootfallEvolution selectedLocation={selectedShop} componentId={componentId} />
              </Grid>

              <Grid size={styles.fullWidth}>
                <FootfallPerEntrance selectedLocation={selectedShop} displayedType={LocationType.Shop} componentId={componentId} />
              </Grid>
            </Grid>
          </IntervalTabsWrapper>
          <SectionTitleWrapper title={"Details"}>
            <Grid container spacing={2}>
              <Grid size={styles.sectionMd6}>
                <WeatherInfluenceOnFootfallComponent selectedLocation={selectedShop} />
              </Grid>
              <Grid size={styles.sectionMd6}>
                <OccupancyLastHourComponent selectedLocation={selectedShop} />
              </Grid>
            </Grid>
          </SectionTitleWrapper>
        </>
      )}
    </>
  );
}

export default ShopDashboard;
